import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, Printer, Send, Save, Copy, Trash2, RefreshCw, Ticket, Image as ImageIcon, X, Pencil } from "lucide-react";
import { GermanDatePicker } from "@/components/ui/date-picker";

const inputCls = "w-full bg-[#141414] border border-white/10 text-white text-sm p-2.5 outline-none focus:border-[#ff0033] placeholder:text-neutral-600";
const labelCls = "block font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase mb-1";
const errMsg = (e) => (e?.response?.data?.detail && typeof e.response.data.detail === "string" ? e.response.data.detail : "Aktion fehlgeschlagen");
const today = () => new Date().toISOString().slice(0, 10);
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";

const emptyForm = (trainer) => ({
  student_name: "", trainer_name: trainer || "", hours: "", value: "", message: "",
  code: "", issue_date: today(), valid_until: "", design: "dark",
});

const MailModal = ({ voucher, onSend, onClose }) => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState("Dein Golftraining-Gutschein");
  const [message, setMessage] = useState(
    `Hallo,\n\nim Anhang findest Du Deinen Gutschein für persönliches Golftraining bei der Golfcoaching Academy Wendlingen.\n\nViel Freude beim Training!\n\nSportliche Grüße\n${voucher.trainer_name || ""}\nGolfcoaching Academy Wendlingen`);
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="voucher-mail-modal">
      <div className="absolute inset-0 bg-black/85" onClick={onClose} />
      <div className="relative bg-[#0A0A0A] border border-[#ff0033]/40 w-full max-w-xl">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <p className="font-mono-lab text-[11px] tracking-widest text-[#ff0033] uppercase">Gutschein per E-Mail versenden</p>
          <button onClick={onClose} className="text-neutral-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block"><span className={labelCls}>Empfänger-E-Mail *</span>
            <input value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="voucher-mail-to" placeholder="name@example.com" /></label>
          <label className="block"><span className={labelCls}>Betreff</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} data-testid="voucher-mail-subject" /></label>
          <label className="block"><span className={labelCls}>Nachricht</span>
            <textarea rows={7} value={message} onChange={(e) => setMessage(e.target.value)} className={inputCls} data-testid="voucher-mail-body" /></label>
          <p className="text-neutral-500 text-xs">Der Gutschein wird automatisch als PDF angehängt.</p>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 text-neutral-300 px-4 py-2.5 font-mono-lab text-xs uppercase">Abbrechen</button>
          <button disabled={busy} data-testid="voucher-mail-send"
            onClick={async () => { setBusy(true); await onSend({ to, subject, message }); setBusy(false); }}
            className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-6 py-2.5 hover:bg-[#cc0029] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Senden
          </button>
        </div>
      </div>
    </div>
  );
};

export const VoucherStudio = ({ client, base, listPath, trainerName = "", canDelete = true }) => {
  const [form, setForm] = useState(emptyForm(trainerName));
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [editId, setEditId] = useState("");
  const [mailFor, setMailFor] = useState(null);
  const formRef = useRef(form);
  formRef.current = form;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ready = form.student_name.trim() && form.trainer_name.trim();

  const loadList = () => client.get(listPath).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (trainerName && !form.trainer_name) set("trainer_name", trainerName); /* eslint-disable-next-line */ }, [trainerName]);

  const render = async (f = formRef.current) => {
    if (!f.student_name.trim() || !f.trainer_name.trim()) { setPreview(""); return; }
    setBusy(true);
    try {
      const res = await client.post(`${base}/render?format=png`, f, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      setPreview((old) => { if (old) URL.revokeObjectURL(old); return u; });
    } catch (e) { toast.error("Vorschau fehlgeschlagen"); } finally { setBusy(false); }
  };

  // Live-Vorschau ~700 ms nach der letzten Eingabe
  useEffect(() => {
    const t = setTimeout(() => render(form), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [form.student_name, form.trainer_name, form.hours, form.value, form.message, form.code, form.issue_date, form.valid_until, form.design]);

  const pdfBlob = async () => {
    const res = await client.post(`${base}/render`, form, { responseType: "blob" });
    return res.data;
  };

  const download = async () => {
    try {
      const blob = await pdfBlob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `Gutschein-${form.student_name.replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 4000);
    } catch { toast.error("Download fehlgeschlagen"); }
  };

  const downloadPng = () => {
    if (!preview) return;
    const a = document.createElement("a");
    a.href = preview;
    a.download = `Gutschein-${form.student_name.replace(/[^A-Za-z0-9]+/g, "-")}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const print = async () => {
    try {
      const blob = await pdfBlob();
      const u = URL.createObjectURL(blob);
      const w = window.open(u, "_blank");
      if (w) setTimeout(() => { try { w.focus(); w.print(); } catch { /* ignore */ } }, 900);
      else toast.error("Pop-up-Blocker verhindert den Druck – bitte PDF herunterladen");
    } catch { toast.error("Druck fehlgeschlagen"); }
  };

  const save = async () => {
    if (!ready) { toast.error("Name des Beschenkten und Trainername sind Pflichtfelder"); return; }
    setSaving(true);
    try {
      if (editId) {
        await client.put(`${listPath}/${editId}`, form);
        toast.success("Gutschein aktualisiert");
      } else {
        const { data } = await client.post(listPath, form);
        setEditId(data.id);
        toast.success("Gutschein gespeichert");
      }
      loadList();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const openItem = (v, { duplicate = false } = {}) => {
    setForm({
      student_name: v.student_name || "", trainer_name: v.trainer_name || trainerName,
      hours: v.hours || "", value: v.value || "", message: v.message || "",
      code: duplicate ? "" : (v.code || ""),
      issue_date: duplicate ? today() : (v.issue_date || today()),
      valid_until: v.valid_until || "",
      design: v.design || "dark",
    });
    setEditId(duplicate ? "" : v.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
    toast.success(duplicate ? "Als Kopie geöffnet – jetzt anpassen und speichern" : "Gutschein geöffnet");
  };

  const removeItem = async (v) => {
    if (!window.confirm(`Gutschein für „${v.student_name}“ löschen?`)) return;
    try {
      await client.delete(`${listPath}/${v.id}`);
      if (editId === v.id) { setEditId(""); }
      toast.success("Gelöscht");
      loadList();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendMail = async (payload) => {
    try {
      let id = editId;
      if (!id) {
        const { data } = await client.post(listPath, form);
        id = data.id;
        setEditId(id);
        loadList();
      }
      const { data } = await client.post(`${listPath}/${id}/send`, payload);
      if (data.ok) { toast.success(`Gutschein an ${data.to} gesendet`); setMailFor(null); loadList(); }
      else toast.error(`Versand fehlgeschlagen (${data.delivery})`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const newVoucher = () => { setForm(emptyForm(trainerName)); setEditId(""); setPreview(""); };
  const chips = useMemo(() => [["1", "90"], ["2", "180"], ["3", "270"], ["5", "450"]], []);

  return (
    <div data-testid="voucher-studio">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono-lab text-[10px] tracking-[0.25em] text-[#ff0033] uppercase">// GUTSCHEIN-GENERATOR</p>
          <h1 className="font-heading text-3xl md:text-4xl uppercase tracking-tight mt-1 text-white">Gutscheine</h1>
          <p className="text-neutral-400 text-sm mt-2 max-w-2xl">In wenigen Sekunden einen hochwertigen Trainingsgutschein im Branding der Academy erstellen – mit virtueller Unterschrift und Golfschul-Stempel. Gutscheincode ist optional.</p>
        </div>
        <button onClick={newVoucher} data-testid="voucher-new" className="border border-white/15 text-neutral-200 hover:border-[#ff0033] font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 flex items-center gap-2">
          <Ticket size={14} /> Neuer Gutschein
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6 items-start">
        {/* Eingaben */}
        <div className="border border-white/10 bg-[#0b0b0b] p-4 space-y-3">
          <div>
            <span className={labelCls}>Gutschein-Design</span>
            <div className="grid grid-cols-2 gap-2" data-testid="voucher-design-select">
              {[
                { id: "dark", label: "Dunkel", desc: "Schwarz · Neon-Rot", swatch: "bg-[#0b0b0b] border-white/20", dot: "bg-white" },
                { id: "light", label: "Weiß", desc: "Hell · Rot-Akzent", swatch: "bg-white border-neutral-300", dot: "bg-[#141414]" },
              ].map((d) => {
                const active = (form.design || "dark") === d.id;
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => set("design", d.id)}
                    data-testid={`voucher-design-${d.id}`}
                    className={`flex items-center gap-3 border p-2.5 text-left transition-colors ${active ? "border-[#ff0033] bg-[#ff0033]/5" : "border-white/10 hover:border-white/30"}`}
                  >
                    <span className={`w-8 h-8 shrink-0 border ${d.swatch} flex items-center justify-center`}>
                      <span className={`w-3 h-3 rounded-full ${d.dot}`} />
                    </span>
                    <span className="min-w-0">
                      <span className="block font-mono-lab text-[11px] uppercase tracking-widest text-white">{d.label}</span>
                      <span className="block text-[10px] text-neutral-500">{d.desc}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block"><span className={labelCls}>Name des Beschenkten *</span>
              <input value={form.student_name} onChange={(e) => set("student_name", e.target.value)} className={inputCls} data-testid="voucher-student" placeholder="z.B. Michael Mustermann" /></label>
            <label className="block"><span className={labelCls}>Trainername *</span>
              <input value={form.trainer_name} onChange={(e) => set("trainer_name", e.target.value)} className={inputCls} data-testid="voucher-trainer" /></label>
            <label className="block"><span className={labelCls}>Trainerstunden (optional)</span>
              <input value={form.hours} onChange={(e) => set("hours", e.target.value)} className={inputCls} data-testid="voucher-hours" placeholder="z.B. 3" /></label>
            <label className="block"><span className={labelCls}>Gutscheinwert € (optional)</span>
              <input value={form.value} onChange={(e) => set("value", e.target.value)} className={inputCls} data-testid="voucher-value" placeholder="z.B. 270" /></label>
          </div>
          <div className="flex flex-wrap gap-2">
            {chips.map(([h, val]) => (
              <button key={h} onClick={() => setForm((f) => ({ ...f, hours: h, value: val }))} data-testid={`voucher-chip-${h}`}
                className="font-mono-lab text-[10px] uppercase tracking-widest border border-white/15 text-neutral-300 hover:border-[#ff0033] hover:text-white px-3 py-1.5">
                {h} Std · {val} €
              </button>
            ))}
            <button onClick={() => setForm((f) => ({ ...f, hours: "", value: "" }))} className="font-mono-lab text-[10px] uppercase tracking-widest text-neutral-500 hover:text-white px-2 py-1.5">zurücksetzen</button>
          </div>
          <label className="block"><span className={labelCls}>Persönlicher Text / Nachricht</span>
            <textarea rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} data-testid="voucher-message"
              placeholder="Viel Spaß beim Training und weiterhin viel Erfolg mit Deinem Golfspiel." /></label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block"><span className={labelCls}>Gutscheincode (optional)</span>
              <input value={form.code} onChange={(e) => set("code", e.target.value)} className={inputCls} data-testid="voucher-code" placeholder="frei wählbar" /></label>
            <label className="block"><span className={labelCls}>Ausstellungsdatum</span>
              <GermanDatePicker value={form.issue_date} onChange={(v) => set("issue_date", v)} placeholder="Datum" className="p-2.5" testId="voucher-issue-date" /></label>
            <label className="block"><span className={labelCls}>Gültig bis (optional)</span>
              <GermanDatePicker value={form.valid_until} onChange={(v) => set("valid_until", v)} placeholder="offen" className="p-2.5" testId="voucher-valid-until" /></label>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
            <button onClick={download} disabled={!ready} data-testid="voucher-download-pdf" className="flex items-center gap-2 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-5 py-2.5 hover:bg-[#cc0029] disabled:opacity-40"><Download size={15} /> PDF herunterladen</button>
            <button onClick={print} disabled={!ready} data-testid="voucher-print" className="flex items-center gap-2 border border-white/15 text-neutral-200 hover:border-[#ff0033] font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-40"><Printer size={14} /> Drucken</button>
            <button onClick={() => setMailFor(form)} disabled={!ready} data-testid="voucher-send-btn" className="flex items-center gap-2 border border-[#ff0033]/60 text-[#ff0033] hover:bg-[#ff0033]/10 font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-40"><Send size={14} /> Per E-Mail</button>
            <button onClick={downloadPng} disabled={!preview} data-testid="voucher-download-png" className="flex items-center gap-2 border border-white/15 text-neutral-300 hover:border-[#ff0033] font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-40"><ImageIcon size={14} /> PNG</button>
            <button onClick={save} disabled={!ready || saving} data-testid="voucher-save" className="flex items-center gap-2 border border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10 font-mono-lab text-[11px] uppercase tracking-widest px-4 py-2.5 disabled:opacity-40">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editId ? "Änderungen speichern" : "Speichern"}
            </button>
          </div>
        </div>

        {/* Live-Vorschau */}
        <div className="border border-white/10 bg-[#0b0b0b] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="font-mono-lab text-[10px] tracking-widest text-neutral-400 uppercase">Live-Vorschau {busy && <span className="text-[#ff0033]">· rendert…</span>}</p>
            <button onClick={() => render(form)} disabled={!ready} data-testid="voucher-refresh" className="flex items-center gap-1 text-neutral-400 hover:text-white font-mono-lab text-[10px] uppercase tracking-widest disabled:opacity-40"><RefreshCw size={12} /> Aktualisieren</button>
          </div>
          {preview ? (
            <img src={preview} alt="Gutschein-Vorschau" data-testid="voucher-preview" className="w-full border border-white/10 shadow-2xl" />
          ) : (
            <div className="aspect-[297/210] flex items-center justify-center text-neutral-500 text-sm font-mono-lab text-center px-6">
              {busy ? <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Gutschein wird erstellt…</span>
                : "Name des Beschenkten und Trainername eingeben – die Vorschau erscheint automatisch."}
            </div>
          )}
        </div>
      </div>

      {/* Historie */}
      <div className="mt-10">
        <h3 className="font-heading text-xl uppercase tracking-tight text-white">Erstellte Gutscheine</h3>
        <p className="text-neutral-500 text-xs mt-1 mb-3">Gespeicherte Gutscheine können erneut geöffnet, bearbeitet, dupliziert, heruntergeladen oder versendet werden.</p>
        {items.length === 0 ? (
          <p className="text-neutral-500 text-sm" data-testid="voucher-history-empty">Noch keine Gutscheine gespeichert.</p>
        ) : (
          <div className="overflow-x-auto border border-white/10" data-testid="voucher-history">
            <table className="w-full text-sm min-w-[860px]">
              <thead><tr className="bg-[#111] text-neutral-500 font-mono-lab text-[10px] uppercase tracking-widest text-left">
                <th className="p-3">Datum</th><th className="p-3">Beschenkter</th><th className="p-3">Trainer</th>
                <th className="p-3">Stunden</th><th className="p-3">Wert</th><th className="p-3">Code</th>
                <th className="p-3">Versand</th><th className="p-3 text-right">Aktionen</th>
              </tr></thead>
              <tbody>
                {items.map((v) => (
                  <tr key={v.id} className={`border-t border-white/5 ${editId === v.id ? "bg-[#ff0033]/5" : ""}`} data-testid={`voucher-row-${v.id}`}>
                    <td className="p-3 text-neutral-400 whitespace-nowrap">{fmt(v.issue_date || v.created_at)}</td>
                    <td className="p-3 text-white">{v.student_name}</td>
                    <td className="p-3 text-neutral-300">{v.trainer_name}</td>
                    <td className="p-3 text-neutral-300">{v.hours || "—"}</td>
                    <td className="p-3 text-neutral-300">{v.value ? `${v.value} €` : "—"}</td>
                    <td className="p-3 font-mono-lab text-[11px] text-[#ff0033]">{v.code || "—"}</td>
                    <td className="p-3 text-neutral-500 text-xs">{v.last_sent_at ? `${v.last_sent_to} · ${fmt(v.last_sent_at)}` : "—"}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => openItem(v)} title="Öffnen & bearbeiten" data-testid={`voucher-open-${v.id}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Pencil size={15} /></button>
                        <button onClick={() => openItem(v, { duplicate: true })} title="Duplizieren" data-testid={`voucher-copy-${v.id}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Copy size={15} /></button>
                        <button onClick={async () => {
                          try {
                            const res = await client.get(`${listPath}/${v.id}/pdf`, { responseType: "blob" });
                            const u = URL.createObjectURL(res.data);
                            const a = document.createElement("a");
                            a.href = u; a.download = `Gutschein-${(v.student_name || "").replace(/[^A-Za-z0-9]+/g, "-")}.pdf`;
                            document.body.appendChild(a); a.click(); a.remove();
                            setTimeout(() => URL.revokeObjectURL(u), 4000);
                          } catch { toast.error("Download fehlgeschlagen"); }
                        }} title="PDF herunterladen" data-testid={`voucher-pdf-${v.id}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Download size={15} /></button>
                        <button onClick={() => { setEditId(v.id); setMailFor(v); }} title="Erneut versenden" data-testid={`voucher-resend-${v.id}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Send size={15} /></button>
                        {canDelete && <button onClick={() => removeItem(v)} title="Löschen" data-testid={`voucher-delete-${v.id}`} className="p-1.5 text-neutral-400 hover:text-[#ff0033]"><Trash2 size={15} /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {mailFor && <MailModal voucher={mailFor} onSend={sendMail} onClose={() => setMailFor(null)} />}
    </div>
  );
};

export default VoucherStudio;
